import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { ChangeInfoTemplate } from '../../templates/ChangeInfoTemplate'
import { ChangePasswordForm, formContexts } from '../../organisms/ChangePasswordForm'
import behaviors from '../../../util/behaviors'
import Lurker from '../../../util/lurker'
import { usePasswordChange } from './passwordContext'
import { bridge } from '../../../util/webview/bridge'

const LURKER_INSTANCES = {
  [formContexts.ChangePassword]: new Lurker('change_password'),
  [formContexts.ForgotPassword]: new Lurker('forgot_password'),
  [formContexts.NewDeviceChangePassword]: new Lurker('new_device_change_password'),
}

const PAGE_CONFIGURATION = {
  [formContexts.ChangePassword]: {
    title: 'Altere sua senha',
    description:
      'Sugerimos uma senha longa e complexa. Por segurança, crie um senha diferente da anterior.',
  },
  [formContexts.ForgotPassword]: {
    title: 'Esqueci minha senha',
    description:
      'Sugerimos uma senha longa e complexa. Por segurança, crie um senha diferente da anterior.',
  },
  [formContexts.NewDeviceChangePassword]: {
    title: 'Criar nova senha',
    description:
      'Sugerimos uma senha longa e complexa. Por segurança, crie um senha diferente da anterior.',
  },
}

const PASSWORD_CONTEXTS_REQUIRING_TOKEN_VALIDATION = [
  formContexts.ForgotPassword,
  formContexts.NewDeviceChangePassword,
]

export const PasswordManagementPage = ({
  token,
  onLinkExpired,
  onBackClick,
  onSuccess,
  passwordContext,
}) => {
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { apiCall, validateToken, apiErrors } = usePasswordChange(token, passwordContext)
  const lurker = LURKER_INSTANCES[passwordContext]
  const pageConfiguration = PAGE_CONFIGURATION[passwordContext]
  const isWebView = bridge?.isWebView() ?? false

  useEffect(() => {
    lurker.pageLoadEvt()
  }, [])

  const handleSubmit = ({ currentPassword, password }) => {
    setIsSubmitting(true)
    return apiCall(password, currentPassword)
      .then(data => {
        lurker.viewMessageEvt('success')

        if (isWebView) {
          bridge.onPasswordChanged?.(true)
        }

        if (typeof onSuccess === 'function') {
          onSuccess(data)
        }
      })
      .catch(error => {
        const [errorType, message, status] = error

        if (isWebView) {
          bridge.onPasswordChanged?.(false)
          bridge.showMessage?.('error', message)
        }

        return handleSubmitError(errorType, message, status)
      })
      .finally(() => setIsSubmitting(false))
  }

  const handleSubmitError = (errorType, message, status) => {
    if (status) return handleExpectedError(errorType, status, message)
    return handleUnexpectedError()
  }

  const handleExpectedError = (errorType, status, message = 'unexpected_status_code') => {
    lurker.viewMessageEvt('error', {
      accounts_reason: message.toLowerCase(),
      accounts_status_code: status,
    })
    setError(errorType)
  }

  const handleUnexpectedError = (errorType, message = 'unknown') => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'unexpected_error',
      accounts_error_message: message,
    })
    setError(errorType)
  }

  useEffect(() => {
    const onInvalidToken = error => {
      switch (error) {
        case apiErrors.RATE_LIMIT_EXCEEDED:
          setError(apiErrors.RATE_LIMIT_EXCEEDED)
          break
        case apiErrors.LINK_EXPIRED:
          onLinkExpired()
          break
        default:
          behaviors.goToErrorPage()
      }
    }

    if (token && PASSWORD_CONTEXTS_REQUIRING_TOKEN_VALIDATION.includes(passwordContext)) {
      validateToken(onInvalidToken)
    }
  }, [token])

  return (
    <ChangeInfoTemplate
      onBackClick={onBackClick}
      title={pageConfiguration.title}
      description={pageConfiguration.description}
      hideHeader={isWebView}
    >
      <ChangePasswordForm
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        formContext={passwordContext}
        errorType={error}
      />
    </ChangeInfoTemplate>
  )
}

PasswordManagementPage.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onLinkExpired: PropTypes.func.isRequired,
  onBackClick: PropTypes.func,
  token: PropTypes.string,
  passwordContext: PropTypes.oneOf(Object.values(formContexts)),
}
